import React, { useEffect, useState } from "react";
import axios from "axios";
import { Box, TextField, Button, Checkbox, FormControlLabel, Typography, CircularProgress, Snackbar, Alert } from "@mui/material";
import { Icon } from "@iconify/react";
import { postDataToAPI } from "../services/ApiService";

// Initialize Toast

const BlackListScan = () => {
  const [email, setEmail] = useState("");
  const [domain, setDomain] = useState("");
  const [tosChecked, setTosChecked] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [errors, setErrors] = useState({});
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const [toast, setToast] = useState({ open: false, message: "", severity: "info" });

  const API_ENDPOINT = "blacklistcheck"; // API URL

  // ✅ Validate form fields
  const validateFields = () => {
    const fieldErrors = {};
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      fieldErrors.email = "Please enter a valid email address.";
    }
    if (!domain || !/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(domain)) {
      fieldErrors.domain = "Please enter a valid domain (e.g., example.com).";
    }
    if (!tosChecked) {
      fieldErrors.tos = "You must agree to the Terms of Service.";
    }

    setErrors(fieldErrors);
    return Object.keys(fieldErrors).length === 0;
  };

  // ✅ Handle reCAPTCHA success
  const handleCaptchaSuccess = (token) => {
    setCaptchaToken(token);
    console.log("Captcha Token:", token);
    setIsSubmitDisabled(false);
  };

  // ✅ Handle form submission & API call
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateFields()) return;
    setLoading(true);
    setErrors({});
    setFormSubmitted(false);

    const payload = {
      email: email,
      domain: domain.replace(/^https?:\/\//, ""),
      'reCaptchaToken':captchaToken
    };
    const result = await postDataToAPI(API_ENDPOINT, payload, setLoading, setToast);
      if (result) setFormSubmitted(true);

    setLoading(false);
  };

  // ✅ Load reCAPTCHA once
  useEffect(() => {
    const loadRecaptcha = () => {
      if (window.grecaptcha) {
        window.grecaptcha.ready(() => {
          window.grecaptcha.render("captcha-container", {
            sitekey: "6Ld8IcIqAAAAAOSN4Isuy7Udbt8VFzTe-5y0vD-D",
            callback: handleCaptchaSuccess,
          });
        });
      }
    };

    if (!document.querySelector("script[src*='recaptcha/enterprise.js']")) {
      const script = document.createElement("script");
      script.src = "https://www.google.com/recaptcha/enterprise.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.onload = loadRecaptcha;
      document.body.appendChild(script);
    } else {
      loadRecaptcha();
    }
  }, []);

  return (
    <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" bgcolor="#f9fafb" p={2} sx={{ paddingBottom: "3rem" }}>
      {/* Top Section */}
      <Box textAlign="center" mb={4}>
        <Typography variant="h2" fontWeight="bold" gutterBottom sx={{ color: "#000" }}>
          <Icon icon="tdesign:system-blocked" width={45} height={45} style={{ marginRight: "16px", color: "rgb(0, 145, 70)" }} />
          Blacklist Checker
        </Typography>
        <Typography variant="body1" sx={{ color: "#333", maxWidth: 800, fontSize: "1.2rem" }}>
          This free online tool scans the provided email domain or IP against all major IP and email blacklists.
        </Typography>
      </Box>

      {/* Form or Submission Result */}
      {!formSubmitted ? (
        <Box component="form" onSubmit={handleSubmit} sx={{ backgroundColor: "#fff", padding: 4, borderRadius: "8px", boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.1)", width: "100%", maxWidth: 800 }}>
          <TextField
            fullWidth
            label="Email"
            type="text"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={Boolean(errors.email)}
            helperText={errors.email}
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { "&.Mui-focused fieldset": { borderColor: "rgb(0, 145, 70)" } } }}
          />
          <TextField
            fullWidth
            label="Domain"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            error={Boolean(errors.domain)}
            helperText={errors.domain}
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { "&.Mui-focused fieldset": { borderColor: "rgb(0, 145, 70)" } } }}
          />
          <FormControlLabel
            control={
              <Checkbox checked={tosChecked} onChange={(e) => setTosChecked(e.target.checked)} sx={{ color: "rgb(0, 145, 70)", "&.Mui-checked": { color: "rgb(0, 145, 70)" } }} />
            }
            label={
              <Typography variant="body2" sx={{ color: "#4b5563" }}>
                I have read and agree to the{" "}
                <a href="https://portend.ai/terms-and-conditions/" target="_blank" style={{ color: "rgb(0, 145, 70)", textDecoration: "none" }}>
                  Terms of Service
                </a>
                .
              </Typography>
            }
          />
          {errors.tos && <Typography variant="body2" color="error" sx={{ mb: 1 }}>{errors.tos}</Typography>}

          {/* reCAPTCHA Container */}
          <div id="captcha-container" style={{ marginBottom: "16px", display: "block", width: "90%" }}></div>

          {errors.api && <Typography variant="body2" color="error" sx={{ mb: 1 }}>{errors.api}</Typography>}

          <Button type="submit" variant="contained" disabled={isSubmitDisabled || loading} sx={{
            mt: 2, backgroundColor: isSubmitDisabled ? "#ccc" : "rgb(0, 145, 70)",
            color: isSubmitDisabled ? "#888" : "#fff", padding: "12px 0",
            width: "300px", fontWeight: "bold",
            "&:hover": { backgroundColor: isSubmitDisabled ? "#ccc" : "rgb(0, 145, 70)" }
          }}>
            {loading ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : "Submit"}
          </Button>
          {(toast.severity === 'error') && <Typography variant="body2" sx={{ my: 2, fontSize:'1.5rem', background:'#ba1111', color: '#fff', padding:'5px', borderRadius:'5px' }}>{toast.message} <IconButton sx={{float:'right', paddingTop:'5px', color:'#fff'}} onClick={()=>setToast({})}><Icon icon="uil:times" width="24" height="24" /></IconButton></Typography>}
        </Box>
      ) : (
        <Box component="div" sx={{ backgroundColor: "#fff", padding: 4, borderRadius: "8px", boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.1)", width: "100%", maxWidth: 800 }}>
          <Typography variant="body1" sx={{ color: "#333", textAlign: "center", fontSize: "1.5rem" }}>
            Thank you. Your request has been submitted. You will be informed by email when the results are available.
          </Typography>
        </Box>
      )}

      <Snackbar open={toast.open} autoHideDuration={5000} onClose={() => setToast({ ...toast, open: false })}>
        <Alert onClose={() => setToast({ ...toast, open: false })} severity={toast.severity} sx={{ width: "100%" }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BlackListScan;
